// Birim varyantı DR3/DR11/DR13 — ForkSetModal'ın "Birim tercihleri" adımı (ForkSetModal ≤600 satır).
// Adım şeridi "Göstergeler → Birim tercihleri → Oluştur" (aday gösterge yoksa orta adım atlanır).
// Aile satırı toplu kısayoldur: yalnız ORTAK geçerli seçenekler (geniş ekranda ≤6 seçenek segment,
// aksi / ≤720 px yerel `<select>`), "N göstergeye uygulanacak · M istisna" + gösterge bazlı istisna
// listesi (UnitSelect; müşteride kayıtlı birim → "müşteri birimi X kullanılıyor" rozeti, aile satırı
// kilitlenmez). Mantık saf `lib/kpi-unit-preferences.ts`'te; bu dosya görünüm + müşteri birimi okuma.
// Müşteri birimleri + kullanım ön kontrolü hafif uçlardan (N+1 yok — TODO-KPI-97).

import type { KpiMetric, KpiReportTableLayout, KpiSetCreate } from "@mahir/shared";
import { getKpiClientUnitsInUse, listKpiClientMetricUnits } from "../../api/kpi";
import { KPI_PACKAGES_COPY } from "../../lib/kpi-packages-copy";
import { KPI_UNIT_COPY } from "../../lib/kpi-unit-copy";
import {
  familySelectedOption,
  familySummary,
  inUseConflicts,
  isUnitPreferenceCandidate,
  sameUnit,
  toUnitPreferences,
  type UnitFamilyRow,
} from "../../lib/kpi-unit-preferences";
import { decisionText, helpText, inputStyle, pillStyle } from "./kpi-form-styles";
import { UnitSelect } from "./UnitSelect";

const F = KPI_UNIT_COPY.fork;
const SEGMENT_MAX = 6;

export type ForkStepKey = "indicators" | "units" | "create";

export function forkSteps(
  selectedMetrics: readonly KpiMetric[],
): { key: ForkStepKey; label: string }[] {
  const units = selectedMetrics.some(isUnitPreferenceCandidate);
  return [
    { key: "indicators", label: F.steps.indicators },
    ...(units ? [{ key: "units" as const, label: F.steps.units }] : []),
    { key: "create", label: F.steps.create },
  ];
}

export function ForkStepStrip(p: {
  steps: readonly { key: ForkStepKey; label: string }[];
  current: ForkStepKey;
}): JSX.Element {
  return (
    <nav aria-label={F.stepsLabel} className="mb-3">
      <ol className="flex flex-wrap items-center gap-2" style={{ ...helpText, listStyle: "none" }}>
        {p.steps.map((s, i) => {
          const active = s.key === p.current;
          return (
            <li key={s.key} className="flex items-center gap-2">
              {i > 0 ? <span aria-hidden="true">→</span> : null}
              <span
                {...(active ? { "aria-current": "step" as const } : {})}
                style={{
                  fontWeight: active ? 700 : 400,
                  color: active ? "var(--color-primary)" : "var(--color-text-2)",
                }}
              >
                {`${(i + 1).toString()}. ${s.label}`}
              </span>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

const unitLabel = (u: string): string => (u.trim() === "" ? KPI_UNIT_COPY.unitless : u);

function FamilyControl(p: {
  row: UnitFamilyRow;
  narrow: boolean;
  disabled?: boolean | undefined;
  onChoose: (key: string, unit: string) => void;
}): JSX.Element {
  const { row } = p;
  const id = `fork-unit-${row.key.replace(/[^a-z0-9-]/gi, "-")}`;
  const selected = familySelectedOption(row);
  if (p.narrow || row.options.length > SEGMENT_MAX) {
    return (
      <div className="grid gap-1">
        <label htmlFor={id} style={helpText}>
          {F.familySelect(row.label)}
        </label>
        <select
          id={id}
          style={{ ...inputStyle, ...decisionText }}
          value={selected}
          disabled={p.disabled}
          onChange={(e) => p.onChoose(row.key, e.target.value)}
        >
          {row.options.map((o) => (
            <option key={o} value={o}>
              {unitLabel(o)}
            </option>
          ))}
        </select>
      </div>
    );
  }
  return (
    <div role="group" aria-label={F.familySelect(row.label)} className="flex flex-wrap gap-1">
      {row.options.map((o) => {
        const on = o === selected;
        return (
          <button
            key={o}
            type="button"
            aria-pressed={on}
            disabled={p.disabled}
            onClick={() => p.onChoose(row.key, o)}
            className="px-3 py-1.5 font-semibold"
            style={{
              ...decisionText,
              borderRadius: 999,
              border: "1px solid var(--color-border)",
              background: on ? "var(--color-primary)" : "var(--color-surface)",
              color: on ? "var(--color-surface)" : "var(--color-text)",
              minHeight: 40,
            }}
          >
            {unitLabel(o)}
          </button>
        );
      })}
    </div>
  );
}

export interface ForkUnitPreferencesViewProps {
  /** null = aileler hesaplanıyor (iskelet) */
  rows: UnitFamilyRow[] | null;
  narrow: boolean;
  loadFailed?: boolean | undefined;
  disabled?: boolean | undefined;
  /** TODO-KPI-97: müşteride değer/hedef taşıyan göstergeler (adımda ön uyarı) */
  inUse?: ReadonlySet<string> | undefined;
  onChoose: (familyKey: string, unit: string) => void;
  onMetricUnit: (familyKey: string, metricId: string, unit: string | null) => void;
}

export function ForkUnitPreferencesView(p: ForkUnitPreferencesViewProps): JSX.Element {
  if (p.rows === null) {
    return (
      <div role="status" aria-busy="true" style={helpText}>
        {F.loading}
      </div>
    );
  }
  return (
    <div className="grid gap-3" data-testid="fork-unit-preferences">
      <p style={helpText}>{F.intro}</p>
      {p.loadFailed ? (
        <p role="status" style={helpText}>
          {F.clientUnitsFailed}
        </p>
      ) : null}
      {p.rows.map((row) => {
        const sum = familySummary(row);
        const conflicts = p.inUse ? inUseConflicts([row], p.inUse) : new Set<string>();
        const exceptionIds = new Set(row.exceptions.map((e) => e.metricId));
        return (
          <section
            key={row.key}
            data-testid="fork-unit-family"
            data-family={row.key}
            className="grid gap-2 rounded-lg p-3"
            style={{ border: "1px solid var(--color-border)" }}
          >
            <div style={{ ...decisionText, fontWeight: 600 }}>{row.label}</div>
            <FamilyControl
              row={row}
              narrow={p.narrow}
              disabled={p.disabled}
              onChoose={p.onChoose}
            />
            <div style={helpText}>{F.applies(sum.applied, sum.exceptions)}</div>
            <details open={sum.exceptions > 0}>
              <summary style={{ ...helpText, cursor: "pointer" }}>
                {F.metricsToggle(row.metrics.length)}
              </summary>
              <ul className="grid gap-2 mt-2" style={{ listStyle: "none", padding: 0 }}>
                {row.metrics.map((m) => (
                  <li
                    key={m.metricId}
                    className="grid gap-1"
                    style={{ gridTemplateColumns: p.narrow ? "1fr" : "minmax(0, 1fr) 220px" }}
                  >
                    <span style={decisionText}>
                      {m.label}
                      {exceptionIds.has(m.metricId) ? (
                        <span style={{ ...pillStyle, marginLeft: 6 }}>{F.exceptionTag}</span>
                      ) : null}
                      {conflicts.has(m.metricId) ? (
                        <span role="note" style={{ ...helpText, display: "block" }}>
                          ⚠ {F.inUseWarn}
                        </span>
                      ) : null}
                      {m.packageUnit !== null && sameUnit(m.unit, m.packageUnit) ? (
                        <span
                          style={{ ...pillStyle, marginLeft: 6 }}
                          data-testid="package-unit-default"
                        >
                          {KPI_PACKAGES_COPY.pills.packageDefault}
                        </span>
                      ) : null}
                    </span>
                    {m.clientUnit !== null ? (
                      <span style={helpText}>
                        <span style={pillStyle}>{F.clientUnitBadge(m.clientUnit)}</span>
                      </span>
                    ) : (
                      <UnitSelect
                        ariaLabel={F.metricUnit(m.label)}
                        value={sameUnit(m.unit, m.defaultUnit) ? null : m.unit}
                        defaultUnit={m.defaultUnit || null}
                        variants={m.variants}
                        current={m.defaultUnit || null}
                        disabled={p.disabled === true}
                        onChange={(u) => p.onMetricUnit(row.key, m.metricId, u)}
                      />
                    )}
                  </li>
                ))}
              </ul>
            </details>
          </section>
        );
      })}
    </div>
  );
}

/**
 * Müşterinin mevcut kalıcı birimleri (metricId → birim): TEK istek (`GET /kpi/clients/:id/
 * metric-units`) — eskiden müşterinin her seti için `getKpiSet` okunuyordu (TODO-KPI-97 N+1).
 */
export async function loadClientUnits(clientId: string): Promise<Map<string, string>> {
  const { units } = await listKpiClientMetricUnits(clientId);
  return new Map(units.filter((u) => u.unit).map((u) => [u.metricId, u.unit]));
}

/** Seçili göstergelerden müşteride değer/hedef taşıyanlar; hata → boş küme (ön uyarı, kapı değil). */
export async function loadMetricsInUse(
  clientId: string,
  metricIds: readonly string[],
): Promise<Set<string>> {
  try {
    return new Set((await getKpiClientUnitsInUse(clientId, metricIds)).inUse);
  } catch {
    return new Set();
  }
}

/**
 * Fork gövdesi; tercih yoksa `unitPreferences`, paket işaretli değilse `packageIds` alanı hiç
 * yazılmaz (paketsiz gövde eski gövdeyle birebir — D12). Üyeliğin tek kaynağı `includeMetricIds`
 * (sunucu paket kalemlerini birleştirmez — DR1); `packageIds` iz / varsayılan / deprecated içindir.
 */
export function forkSetBody(a: {
  name: string;
  frameworkId: string | null;
  layout: KpiReportTableLayout;
  selected: ReadonlySet<string>;
  unitRows: UnitFamilyRow[] | null;
  packageIds?: readonly string[] | undefined;
}): Omit<KpiSetCreate, "clientId"> {
  const prefs = a.unitRows ? toUnitPreferences(a.unitRows) : [];
  const packageIds = a.packageIds ?? [];
  return {
    name: a.name,
    frameworkId: a.frameworkId,
    reportTableLayout: a.layout,
    includeMetricIds: [...a.selected],
    ...(packageIds.length > 0 ? { packageIds: [...packageIds] } : {}),
    ...(prefs.length > 0 ? { unitPreferences: prefs } : {}),
  };
}
